import { useLayoutEffect, useRef, useState } from "react";
import type { ConnectProto } from "@racoon/api";
import type { LocationCustomer } from "../../model/job-map-rows";
import { fittingCount, GAP, LABEL_WIDTH } from "./fitting-count";
import { OrganizationLabel } from "./organization-label";

/**
 * A location's customers side by side, then its provider; customers that don't fit
 * hide behind a «…».
 */
export const CustomerLine = ({
  customers,
  onOpen,
  provider,
}: {
  customers: LocationCustomer[];
  /** Undefined leaves the name plain text. */
  onOpen: (customer: LocationCustomer) => (() => void) | undefined;
  provider?: ConnectProto.Organization;
}) => {
  const lineRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(Infinity);

  useLayoutEffect(() => {
    const line = lineRef.current;
    if (!line) return;
    setWidth(line.clientWidth);
    const observer = new ResizeObserver(() => setWidth(line.clientWidth));
    observer.observe(line);
    return () => observer.disconnect();
  }, []);

  const count = fittingCount(provider ? width - LABEL_WIDTH - GAP : width, customers.length);
  const hidden = customers.slice(count);
  return (
    <div
      ref={lineRef}
      className="flex min-w-0 flex-1 items-center overflow-hidden"
      style={{ gap: GAP }}
    >
      {customers.slice(0, count).map((customer) => (
        <div
          key={String(customer.organization.id)}
          className="min-w-0"
          style={{ flex: `0 1 ${LABEL_WIDTH}px` }}
        >
          <OrganizationLabel organization={customer.organization} onOpen={onOpen(customer)} />
        </div>
      ))}
      {hidden.length > 0 && (
        <span
          className="shrink-0 text-sm text-text-subtle"
          title={hidden.map(({ organization }) => organization.name).join("\n")}
        >
          …
        </span>
      )}
      {provider && (
        <div className="min-w-0" style={{ flex: `0 1 ${LABEL_WIDTH}px` }}>
          <OrganizationLabel organization={provider} />
        </div>
      )}
    </div>
  );
};
